import React, { useState, useEffect, useRef, useMemo } from 'react';
import ProductCard from './ProductCard';

interface Produto {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  imagem_url?: string;
  codigo?: string;
  tempo_preparo: number;
  categoria_nome?: string;
  categoria_id?: number;
  ativo?: boolean;
  opcoes_personalizacao?: any[];
}

interface VirtualizedProductListProps {
  produtos: Produto[];
  produtoDestaqueIndex: number;
  buscaDebounced: string;
  onSelect: (produto: Produto) => void;
  onSwipeLeft?: (produto: Produto) => void;
  onSwipeRight?: (produto: Produto) => void;
  swipeEnabled: boolean;
  visualizacao: 'grade' | 'lista';
  deviceType: 'mobile' | 'ipad' | 'desktop';
}

/**
 * Virtual Scrolling customizado para melhorar performance com muitos produtos
 * Renderiza apenas os produtos visíveis na viewport + buffer
 */
const VirtualizedProductList: React.FC<VirtualizedProductListProps> = ({
  produtos,
  produtoDestaqueIndex,
  buscaDebounced,
  onSelect,
  onSwipeLeft,
  onSwipeRight,
  swipeEnabled,
  visualizacao,
  deviceType
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  
  // Configurações por dispositivo
  const config = useMemo(() => {
    if (deviceType === 'mobile') {
      return {
        itemHeight: visualizacao === 'grade' ? 120 : 100,
        itemsPerRow: 1,
        buffer: 3
      };
    } else if (deviceType === 'ipad') {
      return {
        itemHeight: visualizacao === 'grade' ? 140 : 110,
        itemsPerRow: visualizacao === 'grade' ? 2 : 1,
        buffer: 4
      };
    } else {
      return {
        itemHeight: visualizacao === 'grade' ? 150 : 120,
        itemsPerRow: visualizacao === 'grade' ? 3 : 1,
        buffer: 6
      };
    }
  }, [deviceType, visualizacao]);

  // Calcular produtos visíveis baseado no scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container || produtos.length === 0) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      
      // Calcular índices visíveis
      const startRow = Math.floor(scrollTop / config.itemHeight);
      const startIndex = Math.max(0, (startRow - config.buffer) * config.itemsPerRow);
      
      const visibleRows = Math.ceil(containerHeight / config.itemHeight);
      const endIndex = Math.min(
        produtos.length - 1,
        startIndex + ((visibleRows + config.buffer * 2) * config.itemsPerRow)
      );

      setVisibleRange({ start: startIndex, end: endIndex });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Calcular inicial

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [produtos.length, config.itemHeight, config.buffer]);

  // Produtos visíveis
  const visibleProducts = produtos.slice(visibleRange.start, visibleRange.end + 1);
  
  // Altura total da lista (para scrollbar)
  const totalRows = Math.ceil(produtos.length / config.itemsPerRow);
  const totalHeight = totalRows * config.itemHeight;
  
  // Offset para posicionar produtos visíveis
  const startRowForOffset = Math.floor(visibleRange.start / config.itemsPerRow);
  const offsetY = startRowForOffset * config.itemHeight;

  // Se tiver poucos produtos, renderizar todos (sem virtualização)
  if (produtos.length <= 30) {
    return (
      <div className={`grid gap-3 sm:gap-4 ${
        visualizacao === 'grade' 
          ? deviceType === 'mobile'
            ? 'grid-cols-1'
            : deviceType === 'ipad'
            ? 'grid-cols-2'
            : 'grid-cols-2 xl:grid-cols-3'
          : 'grid-cols-1'
      }`}>
        {produtos.map((produto, index) => {
          const isDestaque = index === produtoDestaqueIndex && buscaDebounced.length > 0;
          return (
            <ProductCard
              key={produto.id}
              produto={produto}
              isDestaque={isDestaque}
              onSelect={() => onSelect(produto)}
              onSwipeLeft={onSwipeLeft ? () => onSwipeLeft(produto) : undefined}
              onSwipeRight={onSwipeRight ? () => onSwipeRight(produto) : undefined}
              swipeEnabled={swipeEnabled}
            />
          );
        })}
      </div>
    );
  }

  // Virtual scrolling para muitos produtos
  return (
    <div
      ref={containerRef}
      className="overflow-y-auto"
      style={{ height: '600px', maxHeight: '70vh' }}
    >
      {/* Container com altura total para scrollbar */}
      <div style={{ height: totalHeight, position: 'relative' }}>
        {/* Produtos visíveis posicionados */}
        <div
          style={{
            transform: `translateY(${offsetY}px)`,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0
          }}
        >
          <div className={`grid gap-3 sm:gap-4 ${
            visualizacao === 'grade' 
              ? deviceType === 'mobile'
                ? 'grid-cols-1'
                : deviceType === 'ipad'
                ? 'grid-cols-2'
                : 'grid-cols-2 xl:grid-cols-3'
              : 'grid-cols-1'
          }`}>
            {visibleProducts.map((produto, localIndex) => {
              const globalIndex = visibleRange.start + localIndex;
              const isDestaque = globalIndex === produtoDestaqueIndex && buscaDebounced.length > 0;
              return (
                <div
                  key={produto.id}
                  style={{ height: config.itemHeight }}
                  className="flex items-stretch"
                >
                  <ProductCard
                    produto={produto}
                    isDestaque={isDestaque}
                    onSelect={() => onSelect(produto)}
                    onSwipeLeft={onSwipeLeft ? () => onSwipeLeft(produto) : undefined}
                    onSwipeRight={onSwipeRight ? () => onSwipeRight(produto) : undefined}
                    swipeEnabled={swipeEnabled}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualizedProductList;
